<template>
  <div class="fixed bottom-5 right-5 z-[90]">
    <button
      v-if="!open"
      class="flex h-14 w-14 items-center justify-center rounded-full bg-blue-500 text-white shadow-xl transition hover:scale-105 hover:bg-blue-600"
      :title="L.cohort_assistant_title"
      @click="openAssistant"
    >
      <ChatBubbleLeftRightIcon class="h-7 w-7" />
    </button>

    <section v-else class="flex h-[min(680px,78vh)] w-[min(420px,calc(100vw-24px))] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
      <header class="flex items-center gap-3 bg-blue-500 px-4 py-3 text-white">
        <div class="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
          <ChatBubbleLeftRightIcon class="h-6 w-6" />
        </div>
        <div class="min-w-0 flex-1">
          <h2 class="font-semibold">{{ L.cohort_assistant_title }}</h2>
          <p class="truncate text-xs text-blue-100">{{ L.cohort_assistant_subtitle }}</p>
        </div>
        <button class="rounded-lg p-1.5 hover:bg-white/15" :title="L.cohort_clear_conversation" @click="resetConversation">
          <ArrowPathIcon class="h-5 w-5" />
        </button>
        <button class="rounded-lg p-1.5 hover:bg-white/15" :title="L.cohort_close" @click="open=false">
          <XMarkIcon class="h-5 w-5" />
        </button>
      </header>

      <div ref="messageArea" class="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4">
        <div v-for="(message,index) in messages" :key="index" class="flex" :class="message.role==='user'?'justify-end':'justify-start'">
          <div class="max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-6" :class="message.role==='user'?'rounded-br-md bg-blue-500 text-white':'rounded-bl-md border border-gray-100 bg-white text-gray-700 shadow-sm'">
            <div class="whitespace-pre-line">{{ message.content }}<span v-if="message.streaming" class="inline-block w-1.5 h-4 ml-0.5 bg-blue-400 animate-pulse align-text-bottom rounded-sm"></span></div>
          </div>
        </div>
      </div>

      <div class="border-t border-gray-100 bg-white p-3">
        <div class="mb-2 flex gap-2 overflow-x-auto pb-1">
          <button v-for="prompt in contextualPrompts" :key="prompt" class="shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-600 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:opacity-40" :disabled="loading" @click="ask(prompt)">{{prompt}}</button>
        </div>
        <form class="flex gap-2" @submit.prevent="submitInput">
          <textarea v-model.trim="input" rows="1" class="min-h-10 flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-50" :placeholder="L.cohort_assistant_placeholder" :disabled="loading" @keydown.enter.exact.prevent="submitInput"></textarea>
          <button class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-40" :disabled="!input||loading" type="submit">
            <PaperAirplaneIcon class="h-5 w-5" />
          </button>
        </form>
        <p class="mt-2 text-center text-[10px] text-gray-400">{{ L.cohort_assistant_disclaimer }}</p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowPathIcon, ChatBubbleLeftRightIcon, PaperAirplaneIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { L } from '@/plugins/utils/i18n';
import { streamAiChat } from '@/modules/cohort/cohortApi.js'

const route = useRoute()
const open = ref(false)
const input = ref('')
const loading = ref(false)
const messageArea = ref(null)
let abortController = null

const welcome = { role: 'assistant', content: L.cohort_assistant_welcome }
const messages = ref([welcome])

const pagePrompts = {
  '/cohort': [L.cohort_prompt_export, L.cohort_prompt_sleep_efficiency, L.cohort_prompt_ahi_normal],
  '/cohort/subjects': [L.cohort_prompt_missing_days, L.cohort_prompt_sleep_latency, L.cohort_prompt_rebind],
  '/cohort/reports': [L.cohort_prompt_filter_person, L.cohort_prompt_hrv_rmssd, L.cohort_prompt_deep_sleep_ratio],
  '/cohorts': [L.cohort_prompt_enter_queue, L.cohort_prompt_mat_principle, L.cohort_prompt_spo2]
}
const contextualPrompts = computed(() => pagePrompts[route.path] || [L.cohort_prompt_mat_principle, L.cohort_prompt_ahi_grading, L.cohort_prompt_export_raw])

function openAssistant() { open.value = true; nextTick(scrollBottom) }

function resetConversation() {
  if (abortController) { abortController.abort(); abortController = null }
  messages.value = [welcome]
  loading.value = false
}

onBeforeUnmount(() => {
  if (abortController) { abortController.abort(); abortController = null }
})

function submitInput() {
  if (!input.value || loading.value) return
  const q = input.value
  input.value = ''
  ask(q)
}

async function ask(question) {
  const normalized = String(question ?? '').trim().slice(0, 500)
  if (!normalized || loading.value) return

  // Add user message
  messages.value.push({ role: 'user', content: normalized })
  nextTick(scrollBottom)

  // Build messages array for API (exclude welcome, only user/assistant with content)
  const apiMessages = messages.value
    .filter(m => m.content && (m.role === 'user' || m.role === 'assistant') && m !== welcome)
    .map(m => ({ role: m.role, content: m.content }))

  // Add streaming assistant placeholder (reactive object for Vue tracking)
  const assistantMsg = reactive({ role: 'assistant', content: '', streaming: true })
  messages.value.push(assistantMsg)
  loading.value = true
  abortController = new AbortController()
  nextTick(scrollBottom)

  try {
    await streamAiChat(
      apiMessages,
      (text) => {
        assistantMsg.content += text
        nextTick(scrollBottom)
      },
      abortController.signal
    )
  } catch (err) {
    if (err.name === 'AbortError') return // User cancelled, ignore
    console.error('[SleepReportAssistant] error:', err)
    if (!assistantMsg.content) {
      assistantMsg.content = err.message === L.cohort_error_rate_limit
        ? L.cohort_error_rate_limit_hint
        : L.cohort_error_answer_failed
    }
  } finally {
    assistantMsg.streaming = false
    loading.value = false
    abortController = null
    nextTick(scrollBottom)
  }
}

function scrollBottom() {
  if (messageArea.value) messageArea.value.scrollTop = messageArea.value.scrollHeight
}
</script>
